import get from 'lodash/get';

import { useInputStore } from '@/features/Sparten';
import type { BaseTariff } from '@/features/Tariffs/types';
import { Role } from '@/features/Clients';
import type { ComparisonServiceField } from '@/features/Comparison/Components/types';

export enum FIELD_PATH_CONTEXT {
  INPUT = 'input',
  TARIFF = 'tariff',
}

export const getEvaluatedFieldPath = (
  fieldPath: ComparisonServiceField['fieldPath'],
  context: FIELD_PATH_CONTEXT,
  role?: Role,
): string =>
  typeof fieldPath === 'function' ? fieldPath(context, role) : fieldPath;

const FALLBACK_VALUE = '-';

export const getFormattedInputValue = (
  field: ComparisonServiceField,
  role?: Role | undefined,
): string => {
  const fieldPath = field.inputFieldPath ?? field.fieldPath;
  const value = get(
    useInputStore(),
    getEvaluatedFieldPath(fieldPath, FIELD_PATH_CONTEXT.INPUT, role),
  ) as unknown;

  if (value === null || value === undefined) {
    return FALLBACK_VALUE;
  }

  if (field.inputFormatter) {
    return field.inputFormatter(value) as string;
  }

  return field.formatter(value) as string;
};

export const getFallbackValue = (
  field: ComparisonServiceField,
  role?: Role,
): string => {
  if (field.inputFieldAsFallback !== undefined && field.inputFieldAsFallback) {
    return getFormattedInputValue(field, role);
  }
  return FALLBACK_VALUE;
};

export const getFormattedTariffValue = <T = BaseTariff>(
  field: ComparisonServiceField,
  tariff: T,
  role?: Role,
): string => {
  const value = get(
    tariff,
    getEvaluatedFieldPath(field.fieldPath, FIELD_PATH_CONTEXT.TARIFF, role),
  );

  if (value === null || value === undefined) {
    return getFallbackValue(field, role);
  }

  let formattedValue = field.formatter(value) as string;
  if (field.customFormatter !== undefined) {
    formattedValue = field.customFormatter(value) as string;
  }

  return formattedValue !== null
    ? formattedValue
    : getFallbackValue(field, role);
};
